import { useState, ChangeEvent, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { SearchInput } from 'UIPackage/MainModule/components/SearchInput';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';

import styles from './SearchKnmPanel.module.scss';
import { SearchKnmFiltersModal } from './SearchKnmFiltersModal';
import { SearchKnmPanelFilters } from './SearchKnmPanelFilters';
import { getKnmSearchSearchString, getKnmSearchTotalCount } from '../selectors/knmSearchSelectors';
import { knmSearchGetData, knmSearchLoadFile, knmSearchSetSearchString } from '../actions/knmSearchActions';

interface ISearchKnmPanelProps {
  dataCount: number;
  searchString: string | undefined;
}

const SearchKnmPanelWithoutStore = ({ dataCount, searchString }: ISearchKnmPanelProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isFiltersModalVisible, setIsFiltersModalVisible] = useState(false);
  const [isSearchInputValid, setIsSearchInputValid] = useState(false);
  const [searchInputError, setSearchInputError] = useState('');

  const closeFiltersModal = useCallback(() => setIsFiltersModalVisible(false), []);
  const handleFilterButtonClick = useCallback(() => setIsFiltersModalVisible(true), []);

  const handleSearchInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      dispatch(knmSearchSetSearchString(value));
      const isValid = value.length > 2;

      setIsSearchInputValid(isValid);
    },
    [dispatch],
  );

  const handleSearchInputSubmit = useCallback(() => {
    if (!isSearchInputValid) {
      setSearchInputError('Длина строки должна быть не менее 3 символов');
    } else {
      setSearchInputError('');
      dispatch(knmSearchGetData());
    }
  }, [dispatch, isSearchInputValid]);

  const handleOnBlur = useCallback(() => {
    !isSearchInputValid ? setSearchInputError('Длина строки должна быть не менее 3 символов') : setSearchInputError('');
  }, [isSearchInputValid]);

  const handleImportClick = (format: ExportFormatsEnum) => () => dispatch(knmSearchLoadFile(format));

  const dropDownOption: IDropdownItem[] = [
    {
      id: 'inOds',
      content: (
        <DropdownItem onClick={handleImportClick(ExportFormatsEnum.ODS)}>Экспорт списка проверок в ODS</DropdownItem>
      ),
    },
    {
      id: 'inXlsx',
      content: (
        <DropdownItem onClick={handleImportClick(ExportFormatsEnum.XLSX)}>Экспорт списка проверок в XLSX</DropdownItem>
      ),
    },
  ];

  return (
    <>
      <div className={styles.FormContainer}>
        <div className={styles.Search}>
          <SearchInput
            className={styles.SearchInput}
            value={searchString}
            name="searchString"
            placeholder="Поиск КНМ"
            buttonDisabled={!isSearchInputValid}
            error={searchInputError}
            onChange={handleSearchInputChange}
            onSubmit={handleSearchInputSubmit}
            onBlur={handleOnBlur}
          />
          <Tooltip
            placement="right"
            content={
              <div className={styles.TooltipContent}>
                Доступен поиск КНМ по следующим критериям:
                <ul>
                  <li>Учетный номер КНМ</li>
                  <li>Номер плана КНМ</li>
                  <li>
                    Дата и номер распоряжения или приказа <br /> руководителя органа контроля о проведении КНМ
                  </li>
                  <li>ИНН или ОГРН проверяемого лица</li>
                  <li>Наименование проверяемого лица</li>
                  <li>Дата начала проведения КНМ</li>
                </ul>
              </div>
            }
          >
            <span className={styles.TooltipIcon} />
          </Tooltip>
        </div>
        <div className={styles.FilterActionBlock}>
          <Button className={styles.FilterButton} onClick={handleFilterButtonClick} color="primary" text="Фильтры" />
          <Dropdown disabled={dataCount === 0} items={dropDownOption} />
        </div>
        <SearchKnmFiltersModal isVisible={isFiltersModalVisible} onClose={closeFiltersModal} />
      </div>
      <SearchKnmPanelFilters />
    </>
  );
};

const mapStateToProps = (state: IRootState) => ({
  dataCount: getKnmSearchTotalCount(state),
  searchString: getKnmSearchSearchString(state),
});

export const SearchKnmPanel = connect(mapStateToProps)(SearchKnmPanelWithoutStore);
